import React, { useState } from 'react';
import { ExternalLink, CheckCircle2, Landmark, User, HelpCircle, ChevronDown } from 'lucide-react';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

// ---------------------------------------------------------------------------
// FACT vs POSITION SYSTEM (Restructure v0.2 Phase B)
// A visually consistent way to distinguish, everywhere on the site:
//   VERIFIED FACT / OFFICIAL SOURCE / STEVE'S POSITION / QUESTION I AM ASKING
//   / DRAFT - STEVE REVIEW REQUIRED
// ---------------------------------------------------------------------------

const BADGES = {
  verified_fact: { label: 'VERIFIED FACT', cls: 'bg-emerald-50 text-emerald-900 border-emerald-300', Icon: CheckCircle2 },
  official_source: { label: 'OFFICIAL SOURCE', cls: 'bg-sky-50 text-sky-900 border-sky-300', Icon: Landmark },
  steves_position: { label: "STEVE'S POSITION", cls: 'bg-[#FBF7EC] text-[#7a611f] border-[#C9A84C]', Icon: User },
  question: { label: 'QUESTION I AM ASKING', cls: 'bg-violet-50 text-violet-900 border-violet-300', Icon: HelpCircle },
  draft: { label: 'DRAFT - STEVE REVIEW REQUIRED', cls: 'bg-amber-50 text-amber-900 border-amber-400', Icon: HelpCircle },
};

export const FactBadge = ({ kind = 'verified_fact' }) => {
  const meta = BADGES[kind] || BADGES.verified_fact;
  const Icon = meta.Icon;
  return (
    <Badge variant="outline" className={`${meta.cls} font-black text-[10px] tracking-wider inline-flex items-center gap-1`} data-testid={`fact-badge-${kind}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
};

// Sourcing standard: SOURCE / YEAR / DATE ACCESSED / LINK (+ optional METHODOLOGY)
export const SourceCite = ({ source, year, accessed, url, methodology }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3" data-testid="source-cite">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
      <span><span className="font-black uppercase tracking-wider text-slate-500">Source:</span> <span className="font-semibold text-slate-700">{source}</span></span>
      {year && <span><span className="font-black uppercase tracking-wider text-slate-500">Year:</span> {year}</span>}
      {accessed && <span><span className="font-black uppercase tracking-wider text-slate-500">Accessed:</span> {accessed}</span>}
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-2" data-testid="source-cite-link">
          View source <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
    {methodology && (
      <Collapsible>
        <CollapsibleTrigger className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-[#1a2744]" data-testid="source-methodology-toggle">
          Methodology <ChevronDown className="h-3 w-3" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <p className="text-xs text-slate-600 leading-relaxed mt-1.5" data-testid="source-methodology-text">{methodology}</p>
        </CollapsibleContent>
      </Collapsible>
    )}
  </div>
);

// "Steve's Position" box: always visually distinct from facts
export const PositionBox = ({ title, children, testId = 'position-box' }) => (
  <div className="bg-[#1a2744] rounded-2xl p-5 sm:p-7" data-testid={testId}>
    <FactBadge kind="steves_position" />
    {title && <p className="mt-3 font-serif font-black text-white text-lg sm:text-xl leading-snug">{title}</p>}
    <div className="mt-2 text-slate-300 text-sm sm:text-base leading-relaxed">{children}</div>
  </div>
);

// "Question I am asking" card
export const QuestionBox = ({ children, testId = 'question-box' }) => (
  <div className="rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3.5" data-testid={testId}>
    <FactBadge kind="question" />
    <p className="mt-2 text-sm font-semibold text-slate-800 leading-relaxed">{children}</p>
  </div>
);

// Verified-fact card with inline citation
export const FactCard = ({ kind = 'verified_fact', title, children, cite }) => (
  <Card data-testid="fact-card">
    <CardContent className="p-5">
      <FactBadge kind={kind} />
      {title && <p className="mt-2.5 font-serif font-bold text-[#1a2744] leading-snug">{title}</p>}
      <div className="mt-1.5 text-sm text-slate-700 leading-relaxed">{children}</div>
      {cite && <div className="mt-3"><SourceCite {...cite} /></div>}
    </CardContent>
  </Card>
);

// Plain-language FAQ accordion
export const FaqList = ({ items, testId = 'faq-list' }) => (
  <Accordion type="single" collapsible className="space-y-2" data-testid={testId}>
    {items.map((f, i) => (
      <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-slate-200 px-4 data-[state=open]:border-[#C9A84C]" data-testid="faq-item">
        <AccordionTrigger className="hover:no-underline py-4 text-left font-serif font-bold text-[#1a2744] text-sm sm:text-base">{f.q}</AccordionTrigger>
        <AccordionContent className="pb-4 text-sm text-slate-700 leading-relaxed">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const DraftBlock = ({ children, testId = 'draft-block' }) => (
  <div className="rounded-xl border border-amber-300 bg-amber-50/70 px-4 py-3.5" data-testid={testId}>
    <FactBadge kind="draft" />
    <div className="mt-2 text-sm text-slate-800 leading-relaxed">{children}</div>
  </div>
);
